import React, { useState } from "react";
import { Box, IconButton, Switch } from "@mui/material";
import { DropTargetMonitor } from "react-dnd";
import {
  useDraggable,
  useDragRef,
} from "@common/components/DragNDrop/Draggable";
import { DragWrapper } from "@common/components/ListDetailViews/ListView/ListCell/header";
import { DragHandle } from "@mui/icons-material";
import { ListDnDTypes } from "@common/components/ListDetailViews/ListView/const";

type ItemProps = {
  id: number;
  checked?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  value: string;
  label: string;
  columns: { key: string; label: string }[];
  draggable?: boolean;
  handleReorder?: (key: number, position: number) => void;
};

const Item = (props: ItemProps) => {
  const {
    id,
    checked = false,
    disabled = false,
    onChange = () => {},
    value,
    label,
    draggable = false,
    columns,
    handleReorder,
  } = props;
  const [hoverPreview, setHoverPreview] = useState<
    "top" | "bottom" | undefined
  >(undefined);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const isDraggable = draggable && !disabled;

  const { dragPreview, dragRef } = useDraggable({
    type: ListDnDTypes.DRAWER_ORDER,
    item: props,
    canDrag: isDraggable,
    isDragging: (monitor) => {
      const draggedItem = monitor.getItem();
      if (!draggedItem || !draggedItem.value) {
        return false;
      }
      const draggedColIndex = columns.findIndex(
        (c) => c.key === draggedItem.value
      );
      if (draggedColIndex < id) setHoverPreview("bottom");
      else if (draggedColIndex > id) setHoverPreview("top");
      else setHoverPreview(undefined);
      return draggedItem.value === props.value;
    },
  });

  const handleDragRef = useDragRef(dragRef);

  const handleDrop = (item: any, monitor: DropTargetMonitor) => {
    if (!handleReorder) return;
    handleReorder(item.id, id);
    setHoverPreview(undefined);
    setIsHovering(false);
  };

  const borderStyle = (position: "top" | "bottom") =>
    isHovering && hoverPreview === position ? "2px solid white" : "none";

  return (
    <Box
      key={id}
      ref={isDraggable ? handleDragRef : undefined}
      sx={{
        my: 0,
        position: "relative",
        borderTop: borderStyle("top"),
        borderBottom: borderStyle("bottom"),
        userSelect: "none",
      }}
      onMouseLeave={() => {
        setHoverPreview(undefined);
        setIsHovering(false);
      }}
    >
      <DragWrapper
        accept={ListDnDTypes.DRAWER_ORDER}
        handleDrop={handleDrop}
        handleHover={() => setIsHovering(true)}
        isDraggable={isDraggable}
      >
        <Box
          sx={{
            my: 0,
            minHeight: "2.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={checked}
              disabled={disabled}
              onChange={(_, value) => onChange(value)}
            />
            {label && <Box component="span">{label}</Box>}
          </Box>
          {isDraggable ? (
            <IconButton
              ref={handleDragRef}
              aria-label="drag"
              size="medium"
              sx={{
                cursor: "grab",
              }}
            >
              <DragHandle fontSize="inherit" />
            </IconButton>
          ) : null}
        </Box>
      </DragWrapper>
    </Box>
  );
};

export default Item;
